import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import { supabase } from '@/lib/supabase';

interface ProductFromDB {
  slug: string;
  name: string;
  subcategory: string | null;
  manufacturer: string;
  origin: string;
  price_min: number | null;
  price_max: number | null;
  description: string | null;
  images: string[] | null;
}

interface ProductUI {
  id: string;
  slug: string;
  name: string;
  subcategory: string;
  manufacturer: string;
  origin: string;
  priceRange: string;
  description: string;
  image: string;
}

function formatPrice(min: number | null, max: number | null): string {
  if (min && max) return `$${min.toLocaleString()} - $${max.toLocaleString()} USD`;
  if (min) return `From $${min.toLocaleString()} USD`;
  if (max) return `Up to $${max.toLocaleString()} USD`;
  return 'Request a Quote';
}

export default function FeaturedProducts() {
  const [products, setProducts] = useState<ProductUI[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFeatured = async () => {
      try {
        const { data } = await supabase
          .from('products')
          .select('slug,name,subcategory,manufacturer,origin,price_min,price_max,description,images')
          .eq('featured', true)
          .order('created_at', { ascending: false })
          .limit(8);

        if (data) {
          setProducts(data.map((p: ProductFromDB) => {
            const imgs = p.images || [];
            const placeholder = 'https://readdy.ai/api/search-image?query=Modern%20industrial%20product%20on%20clean%20white%20studio%20background%20professional%20product%20photography%20clean%20lighting%20isolated%20presentation&width=800&height=600&seq=feat-placeholder&orientation=landscape';
            return {
              id: p.slug,
              slug: p.slug,
              name: p.name,
              subcategory: p.subcategory || '',
              manufacturer: p.manufacturer,
              origin: p.origin,
              priceRange: formatPrice(p.price_min, p.price_max),
              description: p.description || '',
              image: imgs.length > 0 ? imgs[0] : placeholder,
            };
          }));
        }
      } catch {
        // Silently fail - section just won't show
      } finally {
        setLoading(false);
      }
    };
    fetchFeatured();
  }, []);

  if (loading || products.length === 0) return null;

  return (
    <section className="relative py-20 md:py-28 bg-background-50">
      <div className="w-full px-6 md:px-10 lg:px-16">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="flex flex-col md:flex-row md:items-end justify-between mb-14 md:mb-20 gap-4"
        >
          <div>
            <span className="inline-block text-xs font-semibold uppercase tracking-[0.2em] text-accent-600 mb-4">
              Our Catalogue
            </span>
            <h2 className="text-3xl md:text-5xl font-heading font-bold text-foreground-950">
              Featured Products
            </h2>
            <p className="text-foreground-600 max-w-xl mt-3 text-sm md:text-base leading-relaxed">
              Quality products from verified Chinese manufacturers — ready for East African markets.
            </p>
          </div>
          <Link
            to="/products"
            className="inline-flex items-center gap-2 text-sm font-semibold text-accent-600 hover:text-accent-500 transition-colors duration-200 whitespace-nowrap cursor-pointer"
          >
            View All Products
            <i className="ri-arrow-right-line"></i>
          </Link>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {products.map((product, index) => (
            <motion.div
              key={product.id}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: index * 0.12 }}
            >
              <Link
                to={`/products/${product.slug}`}
                className="group bg-background-50 border border-background-200/70 rounded-2xl overflow-hidden hover:border-accent-200/50 transition-all duration-400 cursor-pointer block"
              >
                <div className="relative aspect-[4/3] overflow-hidden bg-background-100">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    loading="lazy"
                  />
                  <span className="absolute top-3 left-3 px-2.5 py-1 bg-background-50/90 backdrop-blur-sm text-[10px] font-semibold uppercase tracking-wider text-foreground-600 rounded-full">
                    {product.subcategory}
                  </span>
                </div>

                <div className="p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-[10px] font-medium text-accent-600 bg-accent-50 px-2 py-0.5 rounded-full uppercase tracking-wider">
                      {product.origin}
                    </span>
                    <span className="text-[10px] text-foreground-400">{product.manufacturer}</span>
                  </div>
                  <h3 className="text-sm font-heading font-semibold text-foreground-900 mb-1.5 leading-snug group-hover:text-primary-600 transition-colors duration-200 line-clamp-2">
                    {product.name}
                  </h3>
                  <p className="text-xs text-foreground-500 mb-4 line-clamp-2 leading-relaxed">
                    {product.description}
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-heading font-bold text-primary-600">{product.priceRange}</span>
                    <span className="text-xs font-medium text-accent-600 group-hover:translate-x-1 transition-transform duration-200 flex items-center gap-1 whitespace-nowrap">
                      Details <i className="ri-arrow-right-s-line"></i>
                    </span>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}